/**
 * Parameters for building an AIP-160 filter string for the SearchAuditEvents API.
 *
 * Supported filterable fields (from proto schema):
 * - object_type (=, !=)
 * - action      (=, !=)
 * - actor.type  (=, !=)
 * - actor.id    (=, !=)
 * - object_id   (=, !=)
 * - create_time (<, <=, >, >=)
 * - search_input(value) — matches object display name and actor display name
 */
export type AuditFilterParams = {
  objectTypes?: string[];
  actions?: string[];
  actorTypes?: string[];
  actorIds?: string[];
  objectId?: string;
  searchInput?: string;
  startTime?: string;
  endTime?: string;
};

/**
 * Builds an AIP-160 filter string from structured params.
 * Returns an empty string when no params are provided (pass `|| undefined` at
 * the call site if you need to omit the field entirely).
 *
 * @example
 * buildAuditFilter({ actions: ['CREATE', 'DELETE'], startTime: '2026-07-08T00:00:00.000Z' })
 * // → '(action="CREATE" OR action="DELETE") AND create_time>="2026-07-08T00:00:00.000Z"'
 */
export function buildAuditFilter(params: AuditFilterParams): string {
  const clauses: string[] = [];

  if (params.objectTypes?.length) {
    clauses.push(buildOrClause('object_type', params.objectTypes));
  }
  if (params.actions?.length) {
    clauses.push(buildOrClause('action', params.actions));
  }
  const actorClause = buildActorClause(params.actorTypes, params.actorIds);
  if (actorClause) {
    clauses.push(actorClause);
  }
  if (params.objectId) {
    clauses.push(`object_id="${params.objectId}"`);
  }
  if (params.searchInput) {
    clauses.push(`search_input("${params.searchInput}")`);
  }
  if (params.startTime) {
    clauses.push(`create_time>="${params.startTime}"`);
  }
  if (params.endTime) {
    clauses.push(`create_time<="${params.endTime}"`);
  }

  return clauses.join(' AND ');
}

function buildOrClause(field: string, values: string[]): string {
  if (values.length === 1) {
    return `${field}="${values[0]}"`;
  }
  return `(${values.map((v) => `${field}="${v}"`).join(' OR ')})`;
}

/**
 * Builds a combined OR clause for actor.type and actor.id, since these two fields represent
 * alternative ways to identify the same logical "actor" filter and should not be AND-ed together.
 *
 * @example
 * buildActorClause(['SYSTEM', 'WEAVE'], ['a656d817-a13e-4576-8cc3-53d3dea07c37'])
 * // → '(actor.type="SYSTEM" OR actor.type="WEAVE" OR actor.id="a656d817-a13e-4576-8cc3-53d3dea07c37")'
 */
function buildActorClause(actorTypes?: string[], actorIds?: string[]): string | undefined {
  const parts = [
    ...(actorTypes ?? []).map((v) => `actor.type="${v}"`),
    ...(actorIds ?? []).map((v) => `actor.id="${v}"`),
  ];

  if (!parts.length) {
    return undefined;
  }
  if (parts.length === 1) {
    return parts[0];
  }
  return `(${parts.join(' OR ')})`;
}
